import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import CustomTable from "../../components/custom-table/custom-table";
import TaskDetailModal from "../../components/modals/taskDetailModal/taskDetailModal";
import {
  parseTasksSelection,
  TasksFilterSelection,
  TASKS_FILTER_NAME,
  toServerFilters,
  withFiltersParam,
} from "./hooks/tasksFilterHelpers";
import { useTasksData } from "./hooks/useTasksData";
import { useTasksFilters } from "./hooks/useTasksFilters";
import { TasksTableHeader } from "./table/tasksTableData";

const Tasks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Parse the shareable filter selection once on mount; it seeds the query.
  const initialSelection = useMemo(
    () => parseTasksSelection(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    tableData,
    loading,
    totalCount,
    handleSearch,
    applyFilters,
    refetch,
    updateItemStatus,
  } = useTasksData({ initialFilters: toServerFilters(initialSelection) });

  const { filterSections, onFilterApply } = useTasksFilters({
    applyFilters,
    initialSelection,
    onSelectionChange: (selection: TasksFilterSelection) =>
      setSearchParams((prev) => withFiltersParam(prev, selection), {
        replace: true,
      }),
  });
  // Selected task row id — opens the detail modal.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  return (
    <div className="tasks__page h-full w-full bg-primaryGray-16 p-2">
      <div className="tasks__inner flex h-full flex-col gap-2 overflow-hidden rounded bg-white p-4">
        <div className="tasks__heading text-h11 font-bold">Tasks</div>
        <div className="tasks__table-container flex-1 overflow-auto">
          <CustomTable
            tableHeaders={TasksTableHeader}
            tableData={tableData}
            itemsPerPage={10}
            pagesPerView={4}
            count={tableData.length}
            totalCount={totalCount}
            isFetching={loading}
            isDisabled={loading}
            tableName={TASKS_FILTER_NAME}
            placeholder="Search by patient name, MRN, title, drug, CMM key, or task ID"
            searchingText={handleSearch}
            onRefreshButton={refetch}
            showRefreshButton={true}
            showFilterButton={true}
            filterSectionsData={filterSections}
            onFilterApplyClicked={onFilterApply}
            onViewClick={(row) => setSelectedTaskId(row?.id ?? null)}
          />
        </div>
      </div>
      <TaskDetailModal
        taskRowId={selectedTaskId}
        onClose={(statusChange) => {
          if (statusChange) {
            updateItemStatus(statusChange.taskRowId, statusChange.taskStatus);
          }
          setSelectedTaskId(null);
        }}
      />
    </div>
  );
};

export default Tasks;
